import {
  Baby,
  BellRing,
  GlassWater,
  Receipt,
  SprayCan,
  Utensils,
  type LucideIcon,
} from "lucide-react";
import type { ar } from "@/lib/i18n/messages";
import type { DoodleBadgeId } from "@/shared/doodles/badges.data";

/**
 * CALL WAITER (D132) — what a diner at a table asked the floor for that is not
 * food. Mirrors the backend's `ServiceRequestKind`; a closed list.
 */
export type ServiceRequestKind =
  | "WAITER"
  | "WATER"
  | "CUTLERY"
  | "BILL"
  | "CLEAN_TABLE"
  | "HIGH_CHAIR";

/** One OPEN ask, as `GET /restaurant/service-requests` returns it. */
export interface ServiceRequest {
  id: string;
  kind: ServiceRequestKind;
  tableId: string;
  tableName: string;
  tableCode: string;
  /** The table's bill when it had exactly one at the time of asking. */
  invoiceId: string | null;
  guestId: string | null;
  requestedAt: string;
  doneAt: string | null;
  doneBySellerId: string | null;
}

/** One glyph per kind — the badge on the tile and the row on the bill. */
export const SERVICE_REQUEST_ICONS: Record<ServiceRequestKind, LucideIcon> = {
  WAITER: BellRing,
  WATER: GlassWater,
  CUTLERY: Utensils,
  BILL: Receipt,
  CLEAN_TABLE: SprayCan,
  HIGH_CHAIR: Baby,
};

/** The Doodles badge on the bill's request card when the table asked for one thing. */
export const SERVICE_REQUEST_BADGES: Record<ServiceRequestKind, DoodleBadgeId> = {
  WAITER: "waiter",
  WATER: "juice",
  CUTLERY: "cutlery",
  BILL: "bill",
  CLEAN_TABLE: "servicebell",
  HIGH_CHAIR: "servicebell",
};

type Pick = (messages: typeof ar) => string;

/** The kind's short name, for the badge and the bill's list. */
export const SERVICE_REQUEST_LABELS: Record<ServiceRequestKind, Pick> = {
  WAITER: (m) => m.serviceRequests.kindWaiter,
  WATER: (m) => m.serviceRequests.kindWater,
  CUTLERY: (m) => m.serviceRequests.kindCutlery,
  BILL: (m) => m.serviceRequests.kindBill,
  CLEAN_TABLE: (m) => m.serviceRequests.kindCleanTable,
  HIGH_CHAIR: (m) => m.serviceRequests.kindHighChair,
};

/** The toast's sentence for a NEW ask, with `{table}` in it. */
export const SERVICE_REQUEST_ALERTS: Record<ServiceRequestKind, Pick> = {
  WAITER: (m) => m.serviceRequests.alertWaiter,
  WATER: (m) => m.serviceRequests.alertWater,
  CUTLERY: (m) => m.serviceRequests.alertCutlery,
  BILL: (m) => m.serviceRequests.alertBill,
  CLEAN_TABLE: (m) => m.serviceRequests.alertCleanTable,
  HIGH_CHAIR: (m) => m.serviceRequests.alertHighChair,
};

/** The open asks grouped by table, each table's oldest first. */
export function serviceRequestsByTable(
  requests: readonly ServiceRequest[] | undefined,
): Map<string, ServiceRequest[]> {
  const out = new Map<string, ServiceRequest[]>();
  for (const request of requests ?? []) {
    const list = out.get(request.tableId);
    if (list) list.push(request);
    else out.set(request.tableId, [request]);
  }
  return out;
}

/**
 * The asks that are NEW since the last look. An ask is new once: a repeat of
 * an open kind is the same row (the server dedupes it), and an ask made again
 * after it was marked done is a new row with a new id — so the id alone says
 * whether the floor has heard it.
 *
 * `seen` is null on the first look, which returns nothing: asks already open
 * when the screen loaded are on the floor's badges, and ringing for all of them
 * on every page load would teach people to mute the chime (D120's rule).
 */
export function freshServiceRequests(
  seen: ReadonlySet<string> | null,
  current: readonly ServiceRequest[],
): ServiceRequest[] {
  if (seen === null) return [];
  return current.filter((request) => !seen.has(request.id));
}
